import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

// The user's preference. `'system'` defers to the OS setting and is the default
// until the user explicitly picks light or dark.
export type ColorMode = 'light' | 'dark' | 'system'

// The mode actually applied to the document — `'system'` always resolves to one
// of these via the `prefers-color-scheme` media query.
export type ResolvedColorMode = 'light' | 'dark'

const STORAGE_KEY = 'atlas-color-mode'

const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

interface IColorModeContext {
  // The user's stored preference (may be `'system'`).
  mode: ColorMode
  // The light/dark mode currently rendered (system preference resolved).
  resolvedMode: ResolvedColorMode
  // Cycles preference: system → light → dark → system.
  toggleColorMode: () => void
  setColorMode: (mode: ColorMode) => void
}

const ColorModeContext = createContext<IColorModeContext | null>(null)

const getSystemMode = (): ResolvedColorMode => {
  try {
    return window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const getInitialMode = (): ColorMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
    // No stored choice (or a legacy value) — default to following the system.
    return 'system'
  } catch {
    return 'system'
  }
}

const resolveMode = (mode: ColorMode, systemMode: ResolvedColorMode): ResolvedColorMode =>
  mode === 'system' ? systemMode : mode

const applyColorMode = (mode: ResolvedColorMode): void => {
  const root = document.documentElement
  if (mode === 'dark') {
    root.setAttribute('data-color-mode', 'dark')
  } else {
    root.removeAttribute('data-color-mode')
  }
}

export const ColorModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<ColorMode>(getInitialMode)
  const [systemMode, setSystemMode] = useState<ResolvedColorMode>(getSystemMode)

  // Track the OS preference so `'system'` mode re-themes live when the user
  // flips their system setting (e.g. macOS auto light/dark at sunset).
  useEffect(() => {
    let mediaQuery: MediaQueryList
    try {
      mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY)
    } catch {
      return
    }
    const handleChange = (event: MediaQueryListEvent) =>
      setSystemMode(event.matches ? 'dark' : 'light')
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const resolvedMode = resolveMode(mode, systemMode)

  useEffect(() => {
    applyColorMode(resolvedMode)
  }, [resolvedMode])

  // Persist the preference (not the resolved value) so `'system'` is remembered.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // ignore unavailable storage (e.g. private mode)
    }
  }, [mode])

  const setColorMode = useCallback((next: ColorMode) => setMode(next), [])
  const toggleColorMode = useCallback(
    () =>
      setMode((prev) => {
        if (prev === 'system') return 'light'
        if (prev === 'light') return 'dark'
        return 'system'
      }),
    [],
  )

  const value = useMemo(
    () => ({ mode, resolvedMode, toggleColorMode, setColorMode }),
    [mode, resolvedMode, toggleColorMode, setColorMode],
  )

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>
}

/** The mode in effect, or light outside a provider (isolated renders, tests). */
export const useResolvedColorMode = (): ResolvedColorMode =>
  useContext(ColorModeContext)?.resolvedMode ?? 'light'

export const useColorMode = (): IColorModeContext => {
  const context = useContext(ColorModeContext)
  if (!context) {
    throw new Error('useColorMode must be used within a ColorModeProvider')
  }
  return context
}
